import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';

/**
 * The shape of {@link SubscriptionStatus.limits} mirrors
 * `public.tiers.limits` (see migration 20260519100000_tier_limits_shape).
 * Every key is required server-side via the `tiers_limits_shape_check`
 * CHECK constraint — the client can rely on them being present.
 */
export interface TierLimits {
  writes_allowed: boolean;
  ebay_sync_allowed: boolean;
  max_active_users: number;
}

export type SubscriptionStatusValue = 'trialing' | 'active' | 'past_due' | 'canceled';

export interface SubscriptionStatus {
  tierId: string;
  status: SubscriptionStatusValue;
  limits: TierLimits;
  /** Convenience: status is 'active' or 'trialing' AND tier writes are allowed. */
  writesAllowed: boolean;
}

interface ShopWithTierRow {
  tier_id: string;
  subscription_status: SubscriptionStatusValue;
  tiers: { limits: TierLimits } | null;
}

/**
 * Source of truth for "may this shop write right now?" in the UI.
 *
 * Shell: any. The data is shop-scoped, not shell-scoped — kiosk routes never
 * call this (they're anonymous and have no writes to gate), but the hook is
 * safe to import from any shell.
 *
 * Contracts:
 *   - RLS: the SELECT runs under the caller's JWT and the `shops_member_select`
 *     policy filters out non-member shops. Non-members see `data === null`.
 *   - Cache: keyed on shopId; staleTime 30s matches the global QueryClient
 *     default. Stripe-webhook-driven status changes (deferred work) will
 *     invalidate this key when they ship.
 *   - The query DOES NOT block on a missing shopId — it returns `enabled: false`
 *     so callers can render a "no shop selected" branch instead of a loading
 *     spinner that never resolves.
 */
export function useSubscriptionStatus(shopId: string | null) {
  return useQuery({
    queryKey: ['subscription-status', shopId],
    enabled: Boolean(shopId),
    queryFn: async (): Promise<SubscriptionStatus | null> => {
      if (!shopId) return null;
      const { data, error } = await supabase
        .from('shops')
        .select('tier_id, subscription_status, tiers(limits)')
        .eq('id', shopId)
        .maybeSingle<ShopWithTierRow>();
      if (error) throw error;
      if (!data?.tiers) return null;
      const subscriptionLive =
        data.subscription_status === 'active' || data.subscription_status === 'trialing';
      return {
        tierId: data.tier_id,
        status: data.subscription_status,
        limits: data.tiers.limits,
        writesAllowed: data.tiers.limits.writes_allowed && subscriptionLive,
      };
    },
  });
}
